import { z } from "zod";
import { TimesheetEntryBaseWageType } from "@api/shared-runtime/enums";
import { format } from "date-fns";
import { Temporal } from "temporal-polyfill";
import { combineDateTimeInTimezone } from "./timezone-utils";
import {
  CUSTOM_WAGE_AMOUNT_ERROR,
  DEFAULT_CUSTOM_WAGE_FREQUENCY,
  isCustomWageFrequency,
  isValidCustomWageAmount,
  type PayWageFrequency,
} from "./custom-wage";

// Re-export for convenience
export { TimesheetEntryBaseWageType };

/**
 * Maximum number of days in the past that ad-hoc time entries can be submitted.
 * This must match the backend constant in create-ad-hoc-shift-with-entry.ts
 */
export const AD_HOC_MAX_DAYS_AGO = 30;

/**
 * Wage type labels - single source of truth for display names.
 * Used by both formatWageType() and wageTypeOptions.
 */
export const WAGE_TYPE_LABELS: Record<TimesheetEntryBaseWageType, string> = {
  [TimesheetEntryBaseWageType.Regular]: "Regular",
  [TimesheetEntryBaseWageType.Weekend]: "Weekend",
  [TimesheetEntryBaseWageType.Holiday]: "Holiday",
  [TimesheetEntryBaseWageType.OnCall]: "On Call",
  [TimesheetEntryBaseWageType.OnCallCallback]: "On Call Callback",
};

/**
 * Format wage type for display.
 */
export function formatWageType(wageType: TimesheetEntryBaseWageType): string {
  return WAGE_TYPE_LABELS[wageType] || wageType;
}

/**
 * Wage type options for dropdown selection.
 * Generated from WAGE_TYPE_LABELS to maintain single source of truth.
 */
export const wageTypeOptions = Object.entries(WAGE_TYPE_LABELS).map(
  ([value, label]) => ({
    value: value as TimesheetEntryBaseWageType,
    label,
  }),
);

/**
 * Parse a time string (HH:mm) into total minutes since midnight.
 * Useful for comparing and validating times.
 */
export function parseTimeToMinutes(timeStr: string): number {
  if (!timeStr) {
    return 0;
  }
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Format a date for HTML date input (YYYY-MM-DD).
 */
export function formatDateForInput(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * Schema for ad-hoc time entry form.
 */
export const adHocTimeEntryFormSchema = z
  .object({
    organizationBusinessUnitId: z
      .string()
      .uuid("Please select an organization"),
    facilityId: z.string().uuid("Please select a facility"),
    organizationWageSetId: z.string().uuid("Please select a wage set"),
    // Keep the calendar date as entered. Coercing YYYY-MM-DD to Date parses it
    // at UTC midnight, which is the prior day in U.S. timezones.
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date is required"),
    startsAt: z.string().min(1, "Start time is required"),
    endsAt: z.string().min(1, "End time is required"),
    baseWageType: z.nativeEnum(TimesheetEntryBaseWageType, {
      required_error: "Please select a wage type",
    }),
    isOvertime: z.boolean().default(false),
    providerNote: z.string().optional(),
    // "Custom wage" choice; the amount only matters while it is on.
    useCustomWage: z.boolean().default(false),
    customWageAmount: z.string().default(""),
    customWageFrequency: z
      .custom<PayWageFrequency>(isCustomWageFrequency)
      .default(DEFAULT_CUSTOM_WAGE_FREQUENCY),
  })
  .refine(
    (data) =>
      !data.useCustomWage || isValidCustomWageAmount(data.customWageAmount),
    {
      message: CUSTOM_WAGE_AMOUNT_ERROR,
      path: ["customWageAmount"],
    },
  )
  .refine(
    (data) => {
      // Start and end times cannot be the same
      if (!data.startsAt || !data.endsAt) {
        return true;
      }
      const startMinutes = parseTimeToMinutes(data.startsAt);
      const endMinutes = parseTimeToMinutes(data.endsAt);
      return startMinutes !== endMinutes;
    },
    {
      message: "Start and end times cannot be the same",
      path: ["endsAt"],
    },
  );

export type AdHocTimeEntryFormInput = z.infer<typeof adHocTimeEntryFormSchema>;

/**
 * Get today's date as max date for the date picker (YYYY-MM-DD format).
 * Today is allowed so providers can submit a shift as soon as it ends; the
 * backend rejects shifts that haven't ended yet.
 */
export function getMaxDateForAdHoc(timezoneId: string): string {
  return Temporal.Now.zonedDateTimeISO(timezoneId).toPlainDate().toString();
}

/**
 * Get minimum date for the date picker (AD_HOC_MAX_DAYS_AGO days ago in YYYY-MM-DD format).
 */
export function getMinDateForAdHoc(timezoneId: string): string {
  return Temporal.Now.zonedDateTimeISO(timezoneId)
    .toPlainDate()
    .subtract({ days: AD_HOC_MAX_DAYS_AGO })
    .toString();
}

/**
 * Combine date and time strings into a UTC ISO timestamp.
 * Handles overnight shifts by adding a day to the end time if needed.
 *
 * @param date - The base date in YYYY-MM-DD format
 * @param timeStr - Time string in HH:mm format
 * @param timezoneId - The timezone of the user entering the date and time
 * @param isEndTime - Whether this is the end time (for overnight shift detection)
 * @param startTimeStr - The start time string (required for overnight detection)
 */
export function combineDateTimeForAdHoc(
  date: string,
  timeStr: string,
  timezoneId: string,
  isEndTime: boolean = false,
  startTimeStr?: string,
): string {
  let calendarDate = date;
  // For overnight shifts, add a day to the end time
  // Detected when end time (in minutes) is strictly less than start time
  if (isEndTime && startTimeStr) {
    const startMinutes = parseTimeToMinutes(startTimeStr);
    const endMinutes = parseTimeToMinutes(timeStr);

    if (endMinutes < startMinutes) {
      calendarDate = Temporal.PlainDate.from(date).add({ days: 1 }).toString();
    }
  }

  return combineDateTimeInTimezone(calendarDate, timeStr, timezoneId);
}
